/**
 * API Service Layer
 * Handles all API calls with JWT token authentication
 */

import axios from 'axios';
import { getToken, removeToken, isTokenValid } from '../utils/jwt';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: false, // Disable cookies for JWT-based auth
});

// Request interceptor to add JWT token to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token && isTokenValid()) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized responses
    if (error.response?.status === 401) {
      console.log('Received 401 - token may be invalid or expired');
      removeToken();
      
      // Redirect to login if not already on login page
      if (!window.location.pathname.includes('login')) {
        window.location.href = `${API_BASE_URL}/login`;
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * API Service Class
 */
class ApiService {
  /**
   * Get user profile information
   * @returns {Promise<object>} User profile data
   */
  async getUserProfile() {
    try {
      const response = await apiClient.get('/api/profile');
      // Handle both old format (direct data) and new format (standardized response)
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      throw error;
    }
  }

  /**
   * Check if user is authenticated (validates JWT token)
   * @returns {Promise<object>} Authentication status
   */
  async checkAuthStatus() {
    try {
      // For JWT-based auth, we validate the token locally
      // and optionally make a lightweight request to verify with server
      const token = getToken();
      
      if (!token || !isTokenValid()) {
        return { authenticated: false };
      }

      // Optional: Make a lightweight request to verify token with server
      // This can be useful for checking if token was revoked server-side
      try {
        const response = await apiClient.get('/api/validate-token');
        // Handle both old format (direct data) and new format (standardized response)
        const data = response.data.data || response.data;
        // New format: { valid: true, authenticated: true, user: {...} }
        return { authenticated: data.valid && data.authenticated, user: data.user || data };
      } catch (error) {
        // If server validation fails, token might be invalid
        if (error.response?.status === 401) {
          removeToken();
          return { authenticated: false };
        }
        throw error;
      }
    } catch (error) {
      console.error('Auth status check failed:', error);
      return { authenticated: false };
    }
  }

  /**
   * Logout user (clear local token)
   * @returns {Promise<void>}
   */
  async logout() {
    try {
      // Clear local token
      removeToken();
      
      // Optional: Notify server about logout
      try {
        await apiClient.post('/api/logout');
      } catch (error) {
        console.warn('Server logout notification failed:', error);
        // Continue with local logout even if server call fails
      }
      
      // Redirect to Azure AD logout for complete logout
      window.location.href = `${API_BASE_URL}/logout`;
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }

  /**
   * Refresh JWT token (if refresh token is available)
   * @returns {Promise<string|null>} New JWT token or null if refresh failed
   */
  async refreshToken() {
    try {
      const response = await apiClient.post('/api/refresh-token');
      const data = response.data.data || response.data;
      const newToken = data.token;
      
      if (newToken) {
        // Store new token
        const { storeToken } = await import('../utils/jwt');
        storeToken(newToken);
        return newToken;
      }
      
      return null;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    }
  }

  /**
   * Get user roles and permissions
   * @returns {Promise<object>} User roles and permissions
   */
  async getUserRoles() {
    try {
      const response = await apiClient.get('/api/user/roles');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to fetch user roles:', error);
      throw error;
    }
  }

  /**
   * Get user groups
   * @returns {Promise<object>} User groups
   */
  async getUserGroups() {
    try {
      const response = await apiClient.get('/api/user/groups');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to fetch user groups:', error);
      throw error;
    }
  }

  // ============================================================================
  // NEW BACKEND ENDPOINTS
  // ============================================================================

  /**
   * Submit answer for a specific day with AI validation
   * @param {object} submissionData - Submission data
   * @returns {Promise<object>} AI validation result
   */
  async submitAnswer(submissionData) {
    try {
      const response = await apiClient.post('/api/submit', submissionData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to submit answer:', error);
      throw error;
    }
  }

  /**
   * Get current user's submissions
   * @returns {Promise<object>} User's submission history
   */
  async getMySubmissions() {
    try {
      const response = await apiClient.get('/api/submissions/my');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to fetch user submissions:', error);
      throw error;
    }
  }

  /**
   * Get calendar configuration
   * @returns {Promise<object>} Calendar metadata and configuration
   */
  async getCalendarConfig() {
    try {
      const response = await apiClient.get('/api/config');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to fetch calendar config:', error);
      throw error;
    }
  }

  /**
   * Get current server date
   * @returns {Promise<object>} Current server date information
   */
  async getCurrentDate() {
    try {
      const response = await apiClient.get('/api/current-date');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to fetch current date:', error);
      throw error;
    }
  }

  /**
   * Get today's assignment for a specific company-game
   * @param {number} companyGameId - Company-game relationship ID
   * @param {string} date - Optional date in YYYY-MM-DD format
   * @returns {Promise<object>} Today's assignment
   */
  async getTodayAssignment(companyGameId, date = null) {
    try {
      const params = { company_game_id: companyGameId };
      if (date) {
        params.date = date;
      }
      const response = await apiClient.get('/api/today', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to fetch today\'s assignment:', error);
      throw error;
    }
  }

  /**
   * Get day content for a specific company-game and date
   * @param {number} companyGameId - Company-game relationship ID
   * @param {string} date - Optional date in YYYY-MM-DD format
   * @returns {Promise<object>} Day content and assignment
   */
  async getDayContent(companyGameId, date = null) {
    try {
      const params = { company_game_id: companyGameId };
      if (date) {
        params.date = date;
      }
      const response = await apiClient.get('/api/day-content', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to fetch day content:', error);
      throw error;
    }
  }

  /**
   * Get user report card
   * @param {number} userId - User ID
   * @returns {Promise<object>} User's performance report
   */
  async getUserReport(userId) {
    try {
      const response = await apiClient.get(`/api/reports/users/${userId}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to fetch user report:', error);
      throw error;
    }
  }

  /**
   * Get today's roster (ADMIN/EDITOR only)
   * @param {number} companyGameId - Company-game relationship ID
   * @returns {Promise<object>} Today's roster with user submission status
   */
  async getTodayRoster(companyGameId) {
    try {
      const response = await apiClient.get('/api/reports/today', {
        params: { company_game_id: companyGameId }
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to fetch today\'s roster:', error);
      throw error;
    }
  }

  /**
   * Get content for specific day using POST request
   * @param {object} contentData - Content request data
   * @param {number} contentData.day - Day number (1-31)
   * @param {number} contentData.gameCompanyId - Optional company-game ID
   * @returns {Promise<object>} Day content
   */
  async getDayContentPost(contentData) {
    try {
      const response = await apiClient.post('/api/content', contentData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to fetch day content via POST:', error);
      throw error;
    }
  }

  /**
   * Get static asset from day folder
   * @param {number} dayNumber - Day number
   * @param {string} filename - Asset filename
   * @returns {Promise<Blob>} Asset file
   */
  async getDayAsset(dayNumber, filename) {
    try {
      const response = await apiClient.get(`/api/days/${dayNumber}/assets/${filename}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch day asset:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export default apiService;
